import React, { useEffect, useState, Component } from 'react';
import { Plus, Edit2, Trash2, Tag, Percent, DollarSign, Gift, ToggleLeft, ToggleRight, Loader2 } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useToast } from '../components/ui/Toast';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
interface Promotion {
  id: string;
  name: string;
  description: string;
  type: 'percentage' | 'fixed' | 'bogo' | 'free_item';
  value: number;
  code?: string;
  minOrderAmount: number;
  maxDiscount: number;
  validFrom: string;
  validTo: string;
  active: boolean;
  usageLimit: number;
  usageCount: number;
  applicableCompanies: any[];
  applicableMenuItems: any[];
  createdBy: string;
  createdAt?: string;
}
export function PromotionsPage() {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Promotion | null>(null);
  const [loading, setLoading] = useState(true);
  const {
    showToast
  } = useToast();
  const {
    addLog
  } = useActivityLog();
  useEffect(() => {
    loadPromotions();
  }, []);
  const loadPromotions = async () => {
    setLoading(true);
    try {
      if (isSupabaseConfigured()) {
        const data = await supabaseClient.getPromotions();
        setPromotions(data);
      } else {
        // Fallback to localStorage
        const saved = localStorage.getItem('criket_promotions');
        setPromotions(saved ? JSON.parse(saved) : []);
      }
    } catch (error) {
      console.error('Error loading promotions:', error);
      const saved = localStorage.getItem('criket_promotions');
      setPromotions(saved ? JSON.parse(saved) : []);
    } finally {
      setLoading(false);
    }
  };
  const handleSave = async (promotion: Omit<Promotion, 'id' | 'usageCount' | 'createdAt'>) => {
    try {
      if (editingPromotion) {
        if (isSupabaseConfigured()) {
          await supabaseClient.updatePromotion(editingPromotion.id, promotion);
        }
        setPromotions(promotions.map(p => p.id === editingPromotion.id ? {
          ...p,
          ...promotion
        } : p));
        addLog('Promoción Actualizada', promotion.name, 'promotion');
        showToast('success', 'Promoción actualizada', `"${promotion.name}" ha sido actualizada`);
      } else {
        const newPromotion = {
          ...promotion,
          id: `promo-${Date.now()}`,
          usageCount: 0
        };
        if (isSupabaseConfigured()) {
          await supabaseClient.createPromotion(newPromotion);
        }
        setPromotions([newPromotion, ...promotions]);
        addLog('Promoción Creada', promotion.name, 'promotion');
        showToast('success', 'Promoción creada', `"${promotion.name}" ha sido creada`);
      }
      await loadPromotions();
      setShowModal(false);
      setEditingPromotion(null);
    } catch (error) {
      console.error('Error saving promotion:', error);
      showToast('error', 'Error', 'No se pudo guardar la promoción');
    }
  };
  const handleDelete = async () => {
    if (deleteConfirm) {
      try {
        if (isSupabaseConfigured()) {
          await supabaseClient.deletePromotion(deleteConfirm.id);
        }
        setPromotions(promotions.filter(p => p.id !== deleteConfirm.id));
        addLog('Promoción Eliminada', deleteConfirm.name, 'promotion');
        showToast('success', 'Promoción eliminada', `"${deleteConfirm.name}" ha sido eliminada`);
        setDeleteConfirm(null);
      } catch (error) {
        console.error('Error deleting promotion:', error);
        showToast('error', 'Error', 'No se pudo eliminar la promoción');
      }
    }
  };
  const toggleActive = async (promotion: Promotion) => {
    try {
      const newActive = !promotion.active;
      if (isSupabaseConfigured()) {
        await supabaseClient.updatePromotion(promotion.id, {
          active: newActive
        });
      }
      setPromotions(promotions.map(p => p.id === promotion.id ? {
        ...p,
        active: newActive
      } : p));
      showToast('success', newActive ? 'Promoción activada' : 'Promoción desactivada');
    } catch (error) {
      console.error('Error toggling promotion:', error);
      showToast('error', 'Error', 'No se pudo cambiar el estado');
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'percentage':
        return <Percent size={20} data-id="element-2260" />;
      case 'fixed':
        return <DollarSign size={20} data-id="element-2261" />;
      case 'bogo':
        return <Gift size={20} data-id="element-2262" />;
      case 'free_item':
        return <Gift size={20} data-id="element-2263" />;
      default:
        return <Tag size={20} data-id="element-2264" />;
    }
  };
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'percentage':
        return 'Porcentaje';
      case 'fixed':
        return 'Monto Fijo';
      case 'bogo':
        return 'Compra 1 Lleva 2';
      case 'free_item':
        return 'Artículo Gratis';
      default:
        return type;
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-2265">
        <div className="text-center" data-id="element-2266">
          <Loader2 className="w-8 h-8 animate-spin text-brand-500 mx-auto mb-4" data-id="element-2267" />
          <p className="text-gray-500 dark:text-gray-400" data-id="element-2268">
            Cargando promociones...
          </p>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-8 max-w-7xl mx-auto" data-id="element-2269">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8" data-id="element-2270">
        <div data-id="element-2271">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-2272">
            Gestión de Promociones
          </h1>
          <p className="text-gray-600 dark:text-gray-400" data-id="element-2273">
            Crea y administra promociones y descuentos
          </p>
        </div>
        <Button leftIcon={<Plus size={18} data-id="element-2275" />} onClick={() => {
        setEditingPromotion(null);
        setShowModal(true);
      }} data-id="element-2274">
          Nueva Promoción
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-id="element-2276">
        <Card className="bg-gradient-to-br from-brand-50 to-brand-100 dark:from-brand-900/20 dark:to-brand-800/20 border-brand-200 dark:border-brand-800" data-id="element-2277">
          <div className="flex items-center justify-between" data-id="element-2278">
            <div data-id="element-2279">
              <p className="text-xs text-brand-600 dark:text-brand-400 font-medium mb-1" data-id="element-2280">
                Total Promociones
              </p>
              <p className="text-3xl font-bold text-brand-900 dark:text-brand-100" data-id="element-2281">
                {promotions.length}
              </p>
            </div>
            <Tag className="text-brand-600 dark:text-brand-400" size={32} data-id="element-2282" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800" data-id="element-2283">
          <div className="flex items-center justify-between" data-id="element-2284">
            <div data-id="element-2285">
              <p className="text-xs text-green-600 dark:text-green-400 font-medium mb-1" data-id="element-2286">
                Activas
              </p>
              <p className="text-3xl font-bold text-green-900 dark:text-green-100" data-id="element-2287">
                {promotions.filter(p => p.active).length}
              </p>
            </div>
            <ToggleRight className="text-green-600 dark:text-green-400" size={32} data-id="element-2288" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-800" data-id="element-2289">
          <div className="flex items-center justify-between" data-id="element-2290">
            <div data-id="element-2291">
              <p className="text-xs text-purple-600 dark:text-purple-400 font-medium mb-1" data-id="element-2292">
                Usos Totales
              </p>
              <p className="text-3xl font-bold text-purple-900 dark:text-purple-100" data-id="element-2293">
                {promotions.reduce((sum, p) => sum + p.usageCount, 0)}
              </p>
            </div>
            <Gift className="text-purple-600 dark:text-purple-400" size={32} data-id="element-2294" />
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800" data-id="element-2295">
          <div className="flex items-center justify-between" data-id="element-2296">
            <div data-id="element-2297">
              <p className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1" data-id="element-2298">
                Con Código
              </p>
              <p className="text-3xl font-bold text-blue-900 dark:text-blue-100" data-id="element-2299">
                {promotions.filter(p => p.code).length}
              </p>
            </div>
            <Tag className="text-blue-600 dark:text-blue-400" size={32} data-id="element-2300" />
          </div>
        </Card>
      </div>

      {/* Promotions List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2301">
        {promotions.length === 0 ? <Card className="col-span-2" data-id="element-2302">
            <div className="text-center py-12" data-id="element-2303">
              <Tag size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" data-id="element-2304" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-2305">
                No hay promociones
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-4" data-id="element-2306">
                Crea tu primera promoción para empezar
              </p>
              <Button onClick={() => setShowModal(true)} leftIcon={<Plus size={18} data-id="element-2308" />} data-id="element-2307">
                Nueva Promoción
              </Button>
            </div>
          </Card> : promotions.map(promotion => <Card key={promotion.id} className="hover:shadow-soft-lg transition-shadow" data-id="element-2309">
              <div className="flex items-start justify-between mb-4" data-id="element-2310">
                <div className="flex items-start gap-3" data-id="element-2311">
                  <div className={`p-3 rounded-xl ${promotion.active ? 'bg-brand-100 dark:bg-brand-900/30 text-brand-600 dark:text-brand-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'}`} data-id="element-2312">
                    {getTypeIcon(promotion.type)}
                  </div>
                  <div data-id="element-2313">
                    <h3 className="font-bold text-gray-900 dark:text-white mb-1" data-id="element-2314">
                      {promotion.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2315">
                      {promotion.description}
                    </p>
                  </div>
                </div>
                <button onClick={() => toggleActive(promotion)} className="shrink-0" data-id="element-2316">
                  {promotion.active ? <ToggleRight className="text-green-600 dark:text-green-400" size={24} data-id="element-2317" /> : <ToggleLeft className="text-gray-400" size={24} data-id="element-2318" />}
                </button>
              </div>

              <div className="space-y-2 mb-4" data-id="element-2319">
                <div className="flex items-center justify-between text-sm" data-id="element-2320">
                  <span className="text-gray-600 dark:text-gray-400" data-id="element-2321">
                    Tipo:
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white" data-id="element-2322">
                    {getTypeLabel(promotion.type)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm" data-id="element-2323">
                  <span className="text-gray-600 dark:text-gray-400" data-id="element-2324">
                    Valor:
                  </span>
                  <span className="font-bold text-brand-600 dark:text-brand-400" data-id="element-2325">
                    {promotion.type === 'percentage' ? `${promotion.value}%` : `RD$ ${promotion.value}`}
                  </span>
                </div>
                {promotion.code && <div className="flex items-center justify-between text-sm" data-id="element-2326">
                    <span className="text-gray-600 dark:text-gray-400" data-id="element-2327">
                      Código:
                    </span>
                    <code className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded font-mono text-xs font-bold" data-id="element-2328">
                      {promotion.code}
                    </code>
                  </div>}
                <div className="flex items-center justify-between text-sm" data-id="element-2329">
                  <span className="text-gray-600 dark:text-gray-400" data-id="element-2330">
                    Usos:
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white" data-id="element-2331">
                    {promotion.usageCount}{' '}
                    {promotion.usageLimit ? `/ ${promotion.usageLimit}` : ''}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm" data-id="element-2332">
                  <span className="text-gray-600 dark:text-gray-400" data-id="element-2333">
                    Vigencia:
                  </span>
                  <span className="text-xs text-gray-600 dark:text-gray-400" data-id="element-2334">
                    {new Date(promotion.validFrom).toLocaleDateString()} -{' '}
                    {new Date(promotion.validTo).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-800" data-id="element-2335">
                <Button variant="outline" size="sm" fullWidth leftIcon={<Edit2 size={14} data-id="element-2337" />} onClick={() => {
            setEditingPromotion(promotion);
            setShowModal(true);
          }} data-id="element-2336">
                  Editar
                </Button>
                <Button variant="ghost" size="sm" leftIcon={<Trash2 size={14} data-id="element-2339" />} onClick={() => setDeleteConfirm(promotion)} className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20" data-id="element-2338">
                  Eliminar
                </Button>
              </div>
            </Card>)}
      </div>

      {/* Promotion Form Modal */}
      {showModal && <PromotionFormModal promotion={editingPromotion} onSave={handleSave} onClose={() => {
      setShowModal(false);
      setEditingPromotion(null);
    }} data-id="element-2340" />}

      {/* Delete Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" data-id="element-2341">
          <Card className="w-full max-w-md" data-id="element-2342">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4" data-id="element-2343">
              Eliminar Promoción
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-6" data-id="element-2344">
              ¿Estás seguro que deseas eliminar{' '}
              <strong data-id="element-2345">"{deleteConfirm.name}"</strong>? Esta acción no se puede
              deshacer.
            </p>
            <div className="flex gap-3" data-id="element-2346">
              <Button variant="outline" onClick={() => setDeleteConfirm(null)} fullWidth data-id="element-2347">
                Cancelar
              </Button>
              <Button onClick={handleDelete} fullWidth className="bg-red-600 hover:bg-red-700" data-id="element-2348">
                Eliminar
              </Button>
            </div>
          </Card>
        </div>}
    </div>;
}
// Promotion Form Modal Component
function PromotionFormModal({
  promotion,
  onSave,
  onClose
}: {
  promotion: Promotion | null;
  onSave: (promotion: Omit<Promotion, 'id' | 'usageCount' | 'createdAt'>) => void;
  onClose: () => void;
}) {
  const [formData, setFormData] = useState({
    name: promotion?.name || '',
    description: promotion?.description || '',
    type: promotion?.type || 'percentage' as const,
    value: promotion?.value || 0,
    code: promotion?.code || '',
    minOrderAmount: promotion?.minOrderAmount || 0,
    maxDiscount: promotion?.maxDiscount || 0,
    validFrom: promotion?.validFrom || new Date().toISOString().split('T')[0],
    validTo: promotion?.validTo || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    active: promotion?.active ?? true,
    usageLimit: promotion?.usageLimit || 0,
    createdBy: promotion?.createdBy || 'Admin'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      ...formData,
      applicableCompanies: [],
      applicableMenuItems: []
    });
  };
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 overflow-y-auto" data-id="element-2349">
      <Card className="w-full max-w-2xl my-8" data-id="element-2350">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6" data-id="element-2351">
          {promotion ? 'Editar Promoción' : 'Nueva Promoción'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4" data-id="element-2352">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-2353">
            <div className="md:col-span-2" data-id="element-2354">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2355">
                Nombre de la Promoción
              </label>
              <input type="text" required value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Ej: Descuento de Verano" data-id="element-2356" />
            </div>

            <div className="md:col-span-2" data-id="element-2357">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2358">
                Descripción
              </label>
              <textarea required rows={2} value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Describe la promoción..." data-id="element-2359" />
            </div>

            <div data-id="element-2360">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2361">
                Tipo de Promoción
              </label>
              <select value={formData.type} onChange={e => setFormData({
              ...formData,
              type: e.target.value as any
            })} className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2362">
                <option value="percentage" data-id="element-2363">Porcentaje de Descuento</option>
                <option value="fixed" data-id="element-2364">Monto Fijo</option>
                <option value="bogo" data-id="element-2365">Compra 1 Lleva 2</option>
                <option value="free_item" data-id="element-2366">Artículo Gratis</option>
              </select>
            </div>

            <div data-id="element-2367">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2368">
                Valor {formData.type === 'percentage' ? '(%)' : '(RD$)'}
              </label>
              <input type="number" required min="0" step="0.01" value={formData.value} onChange={e => setFormData({
              ...formData,
              value: parseFloat(e.target.value)
            })} className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2369" />
            </div>

            <div data-id="element-2370">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2371">
                Código Promocional (Opcional)
              </label>
              <input type="text" value={formData.code} onChange={e => setFormData({
              ...formData,
              code: e.target.value.toUpperCase()
            })} className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-mono" placeholder="VERANO2024" data-id="element-2372" />
            </div>

            <div data-id="element-2373">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2374">
                Pedido Mínimo (RD$)
              </label>
              <input type="number" min="0" step="0.01" value={formData.minOrderAmount} onChange={e => setFormData({
              ...formData,
              minOrderAmount: parseFloat(e.target.value)
            })} className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2375" />
            </div>

            <div data-id="element-2376">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2377">
                Válido Desde
              </label>
              <input type="date" required value={formData.validFrom} onChange={e => setFormData({
              ...formData,
              validFrom: e.target.value
            })} className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2378" />
            </div>

            <div data-id="element-2379">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2380">
                Válido Hasta
              </label>
              <input type="date" required value={formData.validTo} onChange={e => setFormData({
              ...formData,
              validTo: e.target.value
            })} className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2381" />
            </div>

            <div data-id="element-2382">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2383">
                Límite de Usos (0 = ilimitado)
              </label>
              <input type="number" min="0" value={formData.usageLimit} onChange={e => setFormData({
              ...formData,
              usageLimit: parseInt(e.target.value)
            })} className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2384" />
            </div>

            <div data-id="element-2385">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-2386">
                Descuento Máximo (RD$)
              </label>
              <input type="number" min="0" step="0.01" value={formData.maxDiscount} onChange={e => setFormData({
              ...formData,
              maxDiscount: parseFloat(e.target.value)
            })} className="w-full px-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-2387" />
            </div>
          </div>

          <div className="flex gap-3 pt-4" data-id="element-2388">
            <Button type="button" variant="outline" onClick={onClose} fullWidth data-id="element-2389">
              Cancelar
            </Button>
            <Button type="submit" fullWidth data-id="element-2390">
              {promotion ? 'Actualizar' : 'Crear'} Promoción
            </Button>
          </div>
        </form>
      </Card>
    </div>;
}